import React, { useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import { AnyObject } from 'final-form';
import useFetch from 'use-http';

interface IInvoiceOfficeBreakdownDialogProps {
    dialogName: string;
    handleReport?: (data: AnyObject) => void;
    hideDialog: () => void;
}

const labelStyle: React.CSSProperties = { whiteSpace: 'nowrap', width: '9rem' };

const todayIsoDate = () => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${mm}-${dd}`;
};

// The backing procs parse dates with try_convert(..., 101) - MM/DD/YYYY - while the native
// <input type="date"> fields this dialog uses always emit ISO (YYYY-MM-DD). Same helper as
// RevenueReportDialog.tsx.
const isoDateToMMDDYYYY = (isoDate: string) => {
    if (!isoDate) {
        return '';
    }
    const [year, month, day] = isoDate.split('-');
    return `${month}/${day}/${year}`;
};

// The browser's <a download> attribute (used below) wins over any server-side
// Content-Disposition header, so the filename has to be picked here, not just in
// ReportsController.GetInvoiceOfficeBreakdownReport.
const REPORT_FILE_NAMES: Record<string, string> = {
    InvoiceBreakdown: 'InvoiceBreakdown.pdf',
    ClientLMOffice: 'ClientLandman.pdf',
    ClientLMOfficeTotals: 'ClientLandmanWithInvoiceTotals.pdf',
    ClientOfficeTotals: 'ClientWithInvoiceTotals.pdf'
};

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Port of the Oracle Forms "Build Invoice Office Breakdown" screen (invoice_breakdown.fmt) - one
// dialog, a Report Type dropdown picking which of four already-deployed SSRS reports to run
// (InvoiceOfficeBreakdownReport.ReportName), same shared Begin/End Date + Office + Invoice Type
// parameters across all four.
const InvoiceOfficeBreakdownDialog = (props: IInvoiceOfficeBreakdownDialogProps) => {
    const { register, errors, getValues } = useForm();
    const { post, response } = useFetch();
    // The dialog stays open (and unresponsive-looking) for the whole SSRS round-trip - loading
    // drives the OK button's spinner (see FormButton's `loading` prop) so that wait is visible
    // rather than silent, same as InvoiceMaintenancePageComponent.tsx's Generate/Archive buttons.
    const [loading, setLoading] = useState<boolean>(false);

    const OKClicked = async () => {
        const data = getValues();
        const reportOptions = data?.reportOptions;

        setLoading(true);

        try {
            await post('/v1.0/reports/invoice-office-breakdown', {
                reportName: reportOptions?.reportName,
                beginDate: isoDateToMMDDYYYY(reportOptions?.beginDate),
                endDate: isoDateToMMDDYYYY(reportOptions?.endDate),
                office: reportOptions?.office || 'ALL',
                invoiceType: reportOptions?.invoiceType
            });

            if (response.ok) {
                const blob = await response.blob();
                downloadBlobResponseAsFile(blob, REPORT_FILE_NAMES[reportOptions?.reportName] || 'InvoiceBreakdown.pdf');
            }
        } catch (e) {
            console.log('Unable to generate Invoice Office Breakdown report.', e);
        }

        setLoading(false);
        props.handleReport?.(reportOptions);
        props.hideDialog();
    };

    return (
        <Dialog title="Build Invoice Office Breakdown" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form style={{ width: '90%' }}>
                    <RHFInputBox
                        label="Beginning Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.beginDate"
                        type="date"
                        register={register}
                        errors={errors}
                        defaultVal={todayIsoDate()}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Ending Date"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.endDate"
                        type="date"
                        register={register}
                        errors={errors}
                        defaultVal={todayIsoDate()}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFInputBox
                        label="Office"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.office"
                        register={register}
                        errors={errors}
                        defaultVal="ALL"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Report Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.reportName"
                        options={[
                            { value: 'InvoiceBreakdown', label: 'Invoice Breakdown' },
                            { value: 'ClientLMOffice', label: 'Client Landman' },
                            { value: 'ClientLMOfficeTotals', label: 'Client Landman with Invoice Totals' },
                            { value: 'ClientOfficeTotals', label: 'Client with Invoice Totals' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="InvoiceBreakdown"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                    <RHFSelect
                        label="Invoice Type"
                        labelOnLeft={true}
                        labelStyle={labelStyle}
                        name="reportOptions.invoiceType"
                        options={[
                            { value: 'STD', label: 'WIP Not Applied' },
                            { value: 'WIP', label: 'WIP Applied' },
                            { value: 'WIPONLY', label: 'WIP Only' },
                        ]}
                        register={register}
                        errors={errors}
                        defaultVal="STD"
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '100%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{ marginTop: '2rem', justifyContent: 'center' }}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={loading ? 'Generating' : 'OK'}
                    disabled={loading}
                    loading={loading}
                    onClick={() => !loading && OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '18%', marginRight: '1rem' }}
                    text={'Cancel'}
                    disabled={loading}
                    onClick={() => props.hideDialog()}
                />
            </div>
        </Dialog>
    );
};

export default InvoiceOfficeBreakdownDialog;



// WEBPACK FOOTER //
// ./src/components/reports/dialogs/InvoiceOfficeBreakdownDialog.tsx